import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Flame, Zap, LogOut, LayoutDashboard, BookOpen, Terminal, User, Trophy, ShieldAlert,
  Megaphone, Menu, X, Swords, Map as MapIcon, Layers, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";

const ACTIVE = "bg-muted text-foreground border-input";
const IDLE = "text-muted-foreground border-transparent hover:text-foreground hover:bg-muted";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/kurse", label: "Kurse", icon: BookOpen },
  { to: "/playground", label: "Playground", icon: Terminal },
  { to: "/bestenliste", label: "Bestenliste", icon: Trophy },
  { to: "/werbeseite", label: "Werbeseite", icon: Megaphone },
];

const practiceLinks = [
  { to: "/challenges", label: "Challenges", icon: Swords },
  { to: "/lernpfad", label: "Lernpfad", icon: MapIcon },
  { to: "/flashcards", label: "Flashcards", icon: Layers },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    ...links,
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldAlert }] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleMobileNavigate = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-background/85 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          className="md:hidden h-10 w-10 shrink-0 flex items-center justify-center border border-line rounded-lg bg-card text-foreground"
          aria-label="Menü öffnen"
        >
          {menuOpen ? <X className="h-5 w-5" strokeWidth={2.2} /> : <Menu className="h-5 w-5" strokeWidth={2.2} />}
        </button>

        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <Logo />
          <span className="font-heading font-bold text-xl tracking-tight text-foreground hidden sm:block">DevPath</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm border transition-colors ${active ? ACTIVE : IDLE}`}
              >
                <Icon className="h-4 w-4" strokeWidth={2.2} />
                {l.label}
              </Link>
            );
          })}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-uben"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm border transition-colors ${
                  practiceLinks.some((l) => l.to === location.pathname) ? ACTIVE : IDLE
                }`}
              >
                <Swords className="h-4 w-4" strokeWidth={2.2} />
                Üben
                <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="border border-line bg-popover nb-shadow rounded-xl">
              {practiceLinks.map((l) => {
                const Icon = l.icon;
                return (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="flex items-center gap-2 font-bold text-sm cursor-pointer">
                      <Icon className="h-4 w-4" strokeWidth={2.2} />
                      {l.label}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3 ml-auto">
          {user ? (
            <>
              <div className="flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1.5 font-mono text-sm font-semibold" title="Tage in Folge">
                <Flame className="h-4 w-4 text-warning" strokeWidth={2.2} />
                <span data-testid="navbar-streak">{user.streak ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1.5 font-mono text-sm font-semibold" title="XP">
                <Zap className="h-4 w-4 text-primary" strokeWidth={2.2} />
                <span data-testid="navbar-xp">{user.xp ?? 0}</span>
              </div>
              <Link to="/profil" data-testid="nav-profile" aria-label="Profil" className="h-10 w-10 border border-line bg-card rounded-lg flex items-center justify-center text-foreground hover:border-input shrink-0">
                <User className="h-4 w-4" strokeWidth={2.2} />
              </Link>
              <Button
                onClick={handleLogout}
                data-testid="logout-button"
                variant="ghost"
                size="icon"
                className="h-10 w-10 border border-transparent text-muted-foreground hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive rounded-lg shrink-0"
                title="Abmelden"
              >
                <LogOut className="h-4 w-4" strokeWidth={2.2} />
              </Button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-button"
              className="bg-primary font-semibold text-sm px-4 py-2 rounded-lg glow"
            >
              Anmelden
            </Link>
          )}
        </div>
      </div>

      {menuOpen && (
        <nav className="md:hidden border-t border-line bg-card p-3 space-y-1" data-testid="mobile-menu">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            const Icon = l.icon;
            return (
              <button
                key={l.to}
                onClick={() => handleMobileNavigate(l.to)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-semibold text-sm border transition-colors text-left ${active ? ACTIVE : IDLE}`}
              >
                <Icon className="h-4 w-4" strokeWidth={2.2} />
                {l.label}
              </button>
            );
          })}
          <div className="pt-2 mt-2 border-t border-line">
            <p className="px-3 pb-1 eyebrow">Üben</p>
            {practiceLinks.map((l) => {
              const active = location.pathname === l.to;
              const Icon = l.icon;
              return (
                <button
                  key={l.to}
                  onClick={() => handleMobileNavigate(l.to)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-semibold text-sm border transition-colors text-left ${active ? ACTIVE : IDLE}`}
                >
                  <Icon className="h-4 w-4" strokeWidth={2.2} />
                  {l.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </header>
  );
}
